// SPDX-License-Identifier: AGPL-3.0-or-later

import type {VoiceState} from '@app/features/gateway/types/GatewayVoiceTypes';
import Keybind from '@app/features/input/state/InputKeybind';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {getEffectiveAudioState} from '@app/features/voice/engine/VoiceEffectiveAudioState';
import {getLocalMicrophonePublications} from '@app/features/voice/engine/VoiceTrackPublicationUtils';
import LocalVoiceState from '@app/features/voice/state/LocalVoiceState';
import ParticipantVolume from '@app/features/voice/state/ParticipantVolume';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {isVoicePermissionMuteActive} from '@app/features/voice/utils/VoicePermissionUtils';
import {getActiveVoiceProcessingMode} from '@app/features/voice/utils/VoiceProcessingProfile';
import type {Room} from 'livekit-client';

const logger = new Logger('VoiceAudioManager');
const extractUserId = (identity: string): string | null => {
	const match = identity.match(/^user_(\d+)(?:_(.+))?$/);
	return match ? match[1] : null;
};

export function applyLocalAudioPreferencesForUser(userId: string, room: Room | null): void {
	if (!room) {
		logger.warn('No room');
		return;
	}
	room.remoteParticipants.forEach((p) => {
		if (extractUserId(p.identity) !== userId) return;
		try {
			ParticipantVolume.applySettingsToParticipant(p);
		} catch (error) {
			logger.warn(`Failed to apply audio preferences for user ${userId}`, {error});
		}
	});
}

export function applyAllLocalAudioPreferences(room: Room | null): void {
	if (!room) {
		logger.warn('No room');
		return;
	}
	ParticipantVolume.applySettingsToRoom(room);
}

export function isPushToTalkActive(): boolean {
	if (getActiveVoiceProcessingMode(VoiceSettings) === 'studio') return false;
	return Keybind.isPushToTalkEnabled();
}

export function isPushToMuteActive(): boolean {
	if (getActiveVoiceProcessingMode(VoiceSettings) === 'studio') return false;
	return Keybind.isPushToMuteEffective();
}

export function getEffectiveSelfMuteForVoiceStatePayload(): boolean {
	return (
		LocalVoiceState.getSelfMute() ||
		(isPushToTalkActive() && !Keybind.pushToTalkHeld) ||
		(isPushToMuteActive() && Keybind.pushToMuteHeld)
	);
}

export function applyPushToTalkHold(
	held: boolean,
	room: Room | null,
	getCurrentUserVoiceState: () => VoiceState | null,
	syncVoiceState: (partial: {self_mute?: boolean}) => void,
): void {
	Keybind.setPushToTalkHeld(held);
	if (!isPushToTalkActive()) return;
	const serverVoiceState = getCurrentUserVoiceState();
	const audioState = getEffectiveAudioState({
		serverMute: serverVoiceState?.mute ?? false,
		serverDeaf: serverVoiceState?.deaf,
	});
	if (audioState.serverMute || audioState.serverDeaf || audioState.selfDeaf) return;
	const targetMute = audioState.selfMute || !held;
	const ok = setMicrophonePublicationsMuted(room, targetMute);
	if (!ok) {
		syncVoiceState({self_mute: true});
		return;
	}
	syncVoiceState({self_mute: targetMute});
}

export function applyPushToMuteHold(
	held: boolean,
	room: Room | null,
	getCurrentUserVoiceState: () => VoiceState | null,
	syncVoiceState: (partial: {self_mute?: boolean}) => void,
): void {
	Keybind.setPushToMuteHeld(held);
	if (!isPushToMuteActive()) return;
	const serverVoiceState = getCurrentUserVoiceState();
	const audioState = getEffectiveAudioState({
		serverMute: serverVoiceState?.mute ?? false,
		serverDeaf: serverVoiceState?.deaf,
	});
	if (audioState.serverMute || audioState.serverDeaf || audioState.selfDeaf) return;
	const baselineMute = LocalVoiceState.getSelfMute();
	const targetMute = held || baselineMute;
	const ok = setMicrophonePublicationsMuted(room, targetMute);
	if (!ok) {
		syncVoiceState({self_mute: true});
		return;
	}
	syncVoiceState({self_mute: targetMute});
}

export function handlePushToTalkModeChange(
	room: Room | null,
	getCurrentUserVoiceState: () => VoiceState | null,
	syncVoiceState: (partial: {self_mute?: boolean}) => void,
): void {
	Keybind.resetPushToTalkState();
	Keybind.resetPushToMuteState();
	if (Keybind.isPushToTalkEnabled() && LocalVoiceState.getSelfMute()) {
		LocalVoiceState.updateSelfMute(false);
	}
	const serverVoiceState = getCurrentUserVoiceState();
	const audioState = getEffectiveAudioState({
		serverMute: serverVoiceState?.mute ?? false,
		serverDeaf: serverVoiceState?.deaf,
	});
	if (audioState.serverMute || audioState.serverDeaf || audioState.selfDeaf) return;
	const targetMute = getEffectiveSelfMuteForVoiceStatePayload();
	const ok = setMicrophonePublicationsMuted(room, targetMute);
	if (!ok) {
		syncVoiceState({self_mute: true});
		return;
	}
	syncVoiceState({self_mute: targetMute});
}

function setMicrophonePublicationsMuted(room: Room | null, muted: boolean): boolean {
	if (!room?.localParticipant) return true;
	const microphonePublications = getLocalMicrophonePublications(room.localParticipant);
	if (!muted && microphonePublications.length === 0) {
		logger.debug('No microphone publication exists while unmuting; syncing voice state without a local track.');
		return true;
	}
	microphonePublications.forEach((publication) => {
		const operation = muted ? publication.mute() : publication.unmute();
		operation.catch((error) =>
			logger.error(muted ? 'Failed to mute publication' : 'Failed to unmute publication', {error}),
		);
	});
	return true;
}

export type VoiceMuteReason = 'guild' | 'permission' | 'voice_push_to_talk' | 'self' | null;

export function getMuteReason(
	voiceState: VoiceState | null,
	guildId?: string | null,
	channelId?: string | null,
): VoiceMuteReason {
	const isGuildMuted = voiceState?.mute ?? false;
	if (isGuildMuted) return 'guild';
	if (isVoicePermissionMuteActive(voiceState, guildId, channelId)) return 'permission';
	const pttActive = isPushToTalkActive();
	const ptmActive = isPushToMuteActive();
	const baselineSelfMute =
		pttActive || ptmActive ? LocalVoiceState.getSelfMute() : (voiceState?.self_mute ?? LocalVoiceState.getSelfMute());
	const selfMuted = baselineSelfMute || (ptmActive && Keybind.pushToMuteHeld);
	if (selfMuted) return 'self';
	if (pttActive && !Keybind.pushToTalkHeld) return 'voice_push_to_talk';
	return null;
}

export function applyLocalMuteState(
	muted: boolean,
	room: Room | null,
	syncVoiceState: (partial: {self_mute?: boolean}) => void,
): void {
	const targetMute = LocalVoiceState.getSelfDeaf() ? true : muted;
	const currentMute = LocalVoiceState.getSelfMute();
	if (room?.localParticipant) {
		const microphonePublications = getLocalMicrophonePublications(room.localParticipant);
		if (!targetMute && microphonePublications.length === 0) {
			logger.debug('Skipping unmute: no audio tracks exist. Enable microphone first.');
			syncVoiceState({self_mute: true});
			return;
		}
		microphonePublications.forEach((publication) => {
			const operation = targetMute ? publication.mute() : publication.unmute();
			operation.catch((error) =>
				logger.error(targetMute ? 'Failed to mute publication' : 'Failed to unmute publication', {error}),
			);
		});
	}
	if (currentMute !== targetMute) {
		LocalVoiceState.updateSelfMute(targetMute);
		syncVoiceState({self_mute: targetMute});
	}
}
